import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { probationApi } from '../../api/probation';
import { ApiError } from '../../api/client';
import { IconActionButton } from '../../components/IconActionButton';
import { Eye } from 'lucide-react';
import { SectionHeader } from '../../components/SectionHeader';
import { PageHeader } from '../../components/PageHeader';
import { StatusBadge } from '../../components/StatusBadge';
import { DataTable } from '../../components/DataTable';
import { useToast } from '../../components/Toast';
import { EmployeePicker } from '../../components/EmployeePicker';

const STATUS_LABELS: Record<string, string> = {
  in_progress: 'In Progress', under_review: 'Awaiting HR Decision', confirmed: 'Confirmed', extended: 'Extended', non_confirmed: 'Not Confirmed',
};

/** Real HR monitoring - alerts are genuinely computed from actual dates, not placeholders, matching the spec's "approaching expiry / overdue" requirement. */
export function ProbationPage() {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const alertsQuery = useQuery({ queryKey: ['probation-alerts'], queryFn: () => probationApi.alerts() });
  const allQuery = useQuery({ queryKey: ['probation-all'], queryFn: () => probationApi.listAll() });

  const { register, handleSubmit, reset } = useForm<{ employeeId: string; startDate: string; expectedEndDate: string; objectives: string }>();
  const createMutation = useMutation({
    mutationFn: (v: { employeeId: string; startDate: string; expectedEndDate: string; objectives: string }) =>
      probationApi.create({ employeeId: Number(v.employeeId), startDate: v.startDate, expectedEndDate: v.expectedEndDate, objectives: v.objectives ? v.objectives.split('\n').filter(Boolean) : [] }),
    onSuccess: () => { showSuccess('Probation record started.'); queryClient.invalidateQueries({ queryKey: ['probation-all'] }); reset(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to start probation.'),
  });

  return (
    <div className="page">
      <Link to="/hr">&larr; Back to HR Dashboard</Link>
      <PageHeader title="Probation & Confirmation" />

      {alertsQuery.data && (alertsQuery.data.data.expiringSoon.length > 0 || alertsQuery.data.data.overdue.length > 0) && (
        <div className="panel">
          <SectionHeader title="Alerts" />
          <div className="panel-body">
          {alertsQuery.data.data.overdue.length > 0 && (
            <p className="muted" style={{ color: 'var(--color-danger)' }}>
              {alertsQuery.data.data.overdue.length} probation review(s) overdue: {alertsQuery.data.data.overdue.map((r) => r.employee_name).join(', ')}
            </p>
          )}
          {alertsQuery.data.data.expiringSoon.length > 0 && (
            <p className="muted">
              {alertsQuery.data.data.expiringSoon.length} probation(s) ending within 30 days: {alertsQuery.data.data.expiringSoon.map((r) => r.employee_name).join(', ')}
            </p>
          )}
          </div>
        </div>
      )}

      <section className="card">
        <h2>Start New Probation</h2>
        <form className="inline-form" onSubmit={handleSubmit((v) => createMutation.mutate(v))}>
          <label>Employee<EmployeePicker {...register('employeeId', { required: true, valueAsNumber: true })} /></label>
          <label>Start Date<input type="date" {...register('startDate', { required: true })} /></label>
          <label>Expected End Date<input type="date" {...register('expectedEndDate', { required: true })} /></label>
          <label>Objectives (one per line)<textarea rows={2} {...register('objectives')} /></label>
          <button type="submit" disabled={createMutation.isPending}>Start Probation</button>
        </form>
      </section>

      <div className="panel">
        <SectionHeader title="All Probation Records" />
        <div className="panel-body">
        {allQuery.data && (
          <DataTable
            rows={allQuery.data.data}
            rowKey={(r) => r.id}
            emptyMessage="No probation records yet."
            columns={[
              { header: 'Employee', render: (r) => r.employee_name },
              { header: 'Start', render: (r) => r.start_date },
              { header: 'Expected End', render: (r) => r.expected_end_date },
              { header: 'Status', render: (r) => <StatusBadge status={r.status} label={STATUS_LABELS[r.status]} /> },
              { header: '', render: (r) => (
                <span className="row-actions">
                  <IconActionButton icon={Eye} label="View" onClick={() => navigate(`/hr/probation/${r.id}`)} />
                  <Link to={`/hr/staff/${r.employee_id}`}>View in Staff Detail</Link>
                </span>
              ) },
            ]}
          />
        )}
      </div>
      </div>
    </div>
  );
}
